// Module 12 — element clearance. Small, generic "measure a live element's rendered height and publish
// it as a named CSS custom property" utility, used wherever a piece of chrome's real size varies (with
// viewport width, content, or wrapping) and something ELSE needs to reliably clear it without a
// hand-tuned guess. Two call sites in app/index.ts:
//   - the top-centre launcher chip bar (`.wks-launcher`), which WRAPS onto a variable number of rows —
//     `--launcher-clearance` lets the side-drawers' top padding always clear it (adversarial re-audit,
//     2026-07-06 pt.3: measured live, a hidden-scroll launcher was concealing ~83% of its own chips).
//   - the mobile pinned "System totals" header (`.wpanel-totals`), whose real height varies with its
//     live content — `--mobile-totals-clearance` lets the mobile single floating panel (index.html
//     `html.gta-mobile-tabs .wpanel.open:not(.pinned)`) start its top edge below it instead of guessing a
//     fixed offset that could either waste space or, worse, let the floating panel cover the pinned
//     header (P-MOBILE-FUNDAMENTAL, 2026-07-06 pt.4: confirmed live as a real overlap when the two were
//     positioned independently with no shared measurement).
// Pure DOM + an injectable observer, matching perf-hud.ts's style. Absent root ⇒ silent no-op.

export interface ElementClearanceDeps {
  element: HTMLElement | null; // the element to measure; null/absent → no-op
  cssVar: string; // the custom property to publish, e.g. "--launcher-clearance"
  setProperty: (name: string, value: string) => void; // e.g. document.documentElement.style.setProperty
  // injectable ResizeObserver wrapper (tests supply a fake; the real one is `new ResizeObserver(cb)`)
  observe?: (target: HTMLElement, cb: () => void) => { disconnect(): void };
  extraPx?: number; // gap below the element before dependent content starts (default 14)
}

export interface ElementClearance {
  stop(): void;
  refresh(): void; // exposed so tests can step it deterministically
}

export function mountElementClearance(deps: ElementClearanceDeps): ElementClearance {
  const { element } = deps;
  if (!element) return { stop(): void { /* no-op */ }, refresh(): void { /* no-op */ } };
  const extra = deps.extraPx ?? 14;

  const refresh = (): void => {
    const h = element.getBoundingClientRect().height;
    deps.setProperty(deps.cssVar, `${Math.round(h + extra)}px`);
  };

  refresh();
  const observer = deps.observe?.(element, refresh);
  return {
    refresh,
    stop(): void { observer?.disconnect(); },
  };
}
